import React from 'react';
import { Link as ScrollLink } from 'react-scroll';
import { motion } from 'framer-motion';

const Footer = () => {
  return (
    <footer className="bg-marina-900 text-white">
      {/* Wave Divider */}
      <div className="h-16 bg-white relative overflow-hidden">
        <svg
          className="absolute bottom-0 w-full h-16 fill-marina-900"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1200 120"
          preserveAspectRatio="none"
        >
          <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V120H0V95.27C57.71,118.66,131.27,111.48,191.94,96.86,258.75,81.22,320.35,67.9,321.39,56.44Z"></path>
        </svg>
      </div>

      <div className="container mx-auto px-4 md:px-6 pt-12 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Logo and About */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="space-y-4"
          >
            <h3 className="text-2xl font-bold text-white">La Barra Brava</h3>
            <p className="text-marina-200">
              Un paraíso gastronómico donde los sabores del mar cobran vida en cada plato. Disfruta de mariscos frescos en un ambiente único.
            </p>
            <div className="flex space-x-4 pt-2">
              <a 
                href="https://www.facebook.com/profile.php?id=61565513198161" 
                target="_blank" 
                rel="noopener noreferrer" 
                aria-label="Facebook" 
                className="text-marina-200 hover:text-white transition-colors duration-300"
              >
                <i className="fab fa-facebook-f text-xl"></i>
              </a>
              <a 
                href="https://www.instagram.com/labarrabravarestaurante/" 
                target="_blank" 
                rel="noopener noreferrer" 
                aria-label="Instagram" 
                className="text-marina-200 hover:text-white transition-colors duration-300"
              >
                <i className="fab fa-instagram text-xl"></i>
              </a>
              <a 
                href="#" 
                aria-label="TripAdvisor" 
                className="text-marina-200 hover:text-white transition-colors duration-300"
              >
                <i className="fab fa-tripadvisor text-xl"></i>
              </a>
              <a 
                href="#" 
                aria-label="WhatsApp" 
                className="text-marina-200 hover:text-white transition-colors duration-300"
              >
                <i className="fab fa-whatsapp text-xl"></i>
              </a>
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            viewport={{ once: true }}
            className="space-y-4"
          >
            <h3 className="text-xl font-bold text-white mb-6">Enlaces Rápidos</h3>
            <ul className="space-y-3">
              <li>
                <ScrollLink
                  to="menu"
                  spy={true}
                  smooth={true}
                  offset={-80}
                  duration={500}
                  className="text-marina-200 hover:text-white transition-colors duration-300 cursor-pointer flex items-center"
                >
                  <i className="fas fa-chevron-right text-xs mr-2"></i>
                  Menú
                </ScrollLink>
              </li>
              <li>
                <ScrollLink
                  to="about"
                  spy={true}
                  smooth={true}
                  offset={-80}
                  duration={500}
                  className="text-marina-200 hover:text-white transition-colors duration-300 cursor-pointer flex items-center"
                >
                  <i className="fas fa-chevron-right text-xs mr-2"></i>
                  Nuestra Historia
                </ScrollLink>
              </li>
              <li>
                <ScrollLink
                  to="gallery"
                  spy={true}
                  smooth={true}
                  offset={-80}
                  duration={500}
                  className="text-marina-200 hover:text-white transition-colors duration-300 cursor-pointer flex items-center"
                >
                  <i className="fas fa-chevron-right text-xs mr-2"></i>
                  Galería
                </ScrollLink>
              </li>
              <li>
                <ScrollLink
                  to="specials"
                  spy={true}
                  smooth={true}
                  offset={-80}
                  duration={500}
                  className="text-marina-200 hover:text-white transition-colors duration-300 cursor-pointer flex items-center"
                >
                  <i className="fas fa-chevron-right text-xs mr-2"></i>
                  Especiales
                </ScrollLink>
              </li>
              <li>
                <ScrollLink
                  to="contact"
                  spy={true}
                  smooth={true}
                  offset={-80}
                  duration={500}
                  className="text-marina-200 hover:text-white transition-colors duration-300 cursor-pointer flex items-center"
                >
                  <i className="fas fa-chevron-right text-xs mr-2"></i>
                  Reservaciones
                </ScrollLink>
              </li>
            </ul>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true }}
            className="space-y-4"
          >
            <h3 className="text-xl font-bold text-white mb-6">Contáctanos</h3>
            <ul className="space-y-3">
              <li className="flex items-start">
                <i className="fas fa-map-marker-alt text-marina-300 mt-1 mr-3"></i>
                <span className="text-marina-200">
                  Avenida del Mar 1256, Viña del Mar, Chile
                </span>
              </li>
              <li className="flex items-center">
                <i className="fas fa-phone-alt text-marina-300 mr-3"></i>
                <a href="tel:+56987654321" className="text-marina-200 hover:text-white transition-colors duration-300">
                  +56 9 8765 4321
                </a>
              </li>
              <li className="flex items-center">
                <i className="fas fa-envelope text-marina-300 mr-3"></i>
                <a href="mailto:reservas@labarrabrava.cl" className="text-marina-200 hover:text-white transition-colors duration-300">
                  reservas@labarrabrava.cl
                </a>
              </li>
              <li className="flex items-center">
                <i className="fas fa-clock text-marina-300 mr-3"></i>
                <span className="text-marina-200">
                  Lun - Dom: 12:00 - 22:00
                </span>
              </li>
            </ul>
          </motion.div>

          {/* Newsletter */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            viewport={{ once: true }}
            className="space-y-4"
          >
            <h3 className="text-xl font-bold text-white mb-6">Mantente Informado</h3>
            <p className="text-marina-200 mb-4">
              Suscríbete para recibir noticias sobre eventos especiales y ofertas exclusivas.
            </p>
            <form className="flex flex-col space-y-3">
              <input
                type="email"
                placeholder="Tu correo electrónico"
                className="px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-marina-500 text-gray-800"
              />
              <button
                type="submit"
                className="btn-primary py-2"
              >
                Suscribirme
              </button>
            </form>
          </motion.div>
        </div>

        <hr className="border-marina-700 my-10" />

        <div className="flex flex-col md:flex-row justify-between items-center">
          <p className="text-marina-300 text-sm mb-4 md:mb-0">
            &copy; {new Date().getFullYear()} La Barra Brava. Todos los derechos reservados.
          </p>
          <div className="flex space-x-6">
            <a href="#" className="text-marina-300 hover:text-white text-sm transition-colors duration-300">
              Política de Privacidad
            </a>
            <a href="#" className="text-marina-300 hover:text-white text-sm transition-colors duration-300">
              Términos de Servicio
            </a>
            <a href="#" className="text-marina-300 hover:text-white text-sm transition-colors duration-300">
              Mapa del Sitio
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
